import { queryOptions } from '@tanstack/react-query'
import { apiGet, apiPost } from './client'

/**
 * Randul desfacut din lista Remote Connect - perechile lui
 * `Remote_connect::getRemoteTabs / deviceDetails / sessionsJournal /
 * requestDisableConsent / update_access`:
 *
 *   GET  /remote-connect/device/{id}/tabs
 *   GET  /remote-connect/device/{id}/details
 *   GET  /remote-connect/device/{id}/journal
 *   GET  /remote-connect/device/{id}/permissions
 *   POST /remote-connect/device/{id}/permissions
 *
 * Drepturile filelor si masina in firmele omului se verifica pe SERVER;
 * ecranul deseneaza ce primeste.
 */

export type RemoteConnectTab = { key: 'details' | 'journal' | 'permissions'; label: string; href: string }

export type RemoteConnectTabsPage = {
  device: { id: number; name: string }
  tool: 'screenconnect' | 'rustdesk'
  tabs: RemoteConnectTab[]
}

export type RemoteConnectDetailsPage = {
  tool: 'screenconnect' | 'rustdesk'
  /** `null` pe ScreenConnect: evidenta sesiunilor e la el, rubricile raman goale. */
  summary: {
    total: number
    totalSeconds: number
    totalText: string
    lastStarted: string | null
    lastEnded: string | null
    inProgress: boolean
    lastOperator: string
  } | null
}

export type RemoteConnectJournalFile = {
  takenFromDevice: boolean
  name: string
  bytesText: string
  happenedAt: string
}

export type RemoteConnectJournalSession = {
  id: number
  startedAt: string
  stealth: boolean
  inProgress: boolean
  durationText: string
  /** Inchisa de cron, nu de agent: durata e o presupunere. */
  presumed: boolean
  operator: string
  account: string
  ticket: number | null
  files: RemoteConnectJournalFile[]
}

export type RemoteConnectJournalPage = {
  tool: 'screenconnect' | 'rustdesk'
  limit: number
  truncated: boolean
  sessions: RemoteConnectJournalSession[]
}

export type RemoteConnectPermissionsPage = {
  device: { id: number; name: string }
  tool: 'screenconnect' | 'rustdesk'
  remoteConnectAccess: boolean
  rustdesk: { consent: 'inherit' | 'ask' | 'direct'; companyAsks: boolean } | null
  screenconnect: { companyDisableRemoteConsent: boolean; disabledSecondsLeft: number } | null
}

export function remoteConnectTabsQuery(id: number) {
  return queryOptions({
    queryKey: ['remote-connect', 'device', id, 'tabs'],
    queryFn: () => apiGet<RemoteConnectTabsPage>(`/remote-connect/device/${id}/tabs`),
  })
}

export function remoteConnectDetailsQuery(id: number) {
  return queryOptions({
    queryKey: ['remote-connect', 'device', id, 'details'],
    queryFn: () => apiGet<RemoteConnectDetailsPage>(`/remote-connect/device/${id}/details`),
  })
}

export function remoteConnectJournalQuery(id: number) {
  return queryOptions({
    queryKey: ['remote-connect', 'device', id, 'journal'],
    queryFn: () => apiGet<RemoteConnectJournalPage>(`/remote-connect/device/${id}/journal`),
  })
}

export function remoteConnectPermissionsQuery(id: number) {
  return queryOptions({
    queryKey: ['remote-connect', 'device', id, 'permissions'],
    queryFn: () => apiGet<RemoteConnectPermissionsPage>(`/remote-connect/device/${id}/permissions`),
  })
}

/** Formularul "Remote Access" din `remote_connect/settings.php`, aceleasi campuri. */
export function saveRemoteConnectPermissions(
  id: number,
  fields: { remote_connect_access: boolean; rustdesk_consent?: 'inherit' | 'ask' | 'direct' },
): Promise<RemoteConnectPermissionsPage & { saved: boolean }> {
  const body: Record<string, string | number> = { device_id: id }
  if (fields.remote_connect_access) body.remote_connect_access = 'on'
  if (fields.rustdesk_consent) body.rustdesk_consent = fields.rustdesk_consent
  return apiPost(`/remote-connect/device/${id}/permissions`, body)
}
